/**
 * Screen-space effects of the CAD render profile. They run as independent post passes over the resolved layer
 * color and depth, so they need no extra vertex attributes and leave the scene shaders untouched.
 */
export interface HsCadEffectsSettings {
  ssaoEnabled: boolean;
  /** Occlusion strength, 0 to 2; values above 1 deepen the occlusion instead of only blending it in. */
  ssaoIntensity: number;
  /** Sampling radius as a fraction of the viewport height, 0.005 to 0.08. */
  ssaoRadius: number;
  edgesEnabled: boolean;
  /** Edge darkness, 0 to 1. */
  edgeIntensity: number;
  /** Edge thickness in pixels: 1 or 2. */
  edgeWidth: number;
  tonemapEnabled: boolean;
  /** Share of the filmic curve blended over the input color, 0 to 1. */
  tonemapStrength: number;
  /** Color saturation, 0 (grey) to 2. */
  saturation: number;
  /** White balance shift, -1 (cool) to 1 (warm). */
  warmth: number;
  /** Camera-relative key light from the upper left, 0 to 1; gives surfaces studio-like modelling. */
  keyLight: number;
  /** Unsharp-mask amount, 0 to 1. */
  sharpen: number;
  gradientBackground: boolean;
  /** Radial darkening toward the viewport corners on the gradient background. */
  vignette: boolean;
  /** Ambient occlusion resolution scale while the camera moves: 0.5 (half) or 1. */
  navigationScale: 0.5 | 1;
}

export const HS_DEFAULT_CAD_EFFECTS_SETTINGS: Readonly<HsCadEffectsSettings> = {
  ssaoEnabled: true,
  ssaoIntensity: 1,
  ssaoRadius: 0.03,
  edgesEnabled: true,
  edgeIntensity: 0.55,
  edgeWidth: 1,
  tonemapEnabled: true,
  tonemapStrength: 0.55,
  saturation: 1,
  warmth: 0,
  keyLight: 0,
  sharpen: 0,
  gradientBackground: true,
  vignette: false,
  navigationScale: 0.5,
};
